"use client";

import posthog from "posthog-js";
import { useEffect } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Global Error Boundary
 *
 * Catches errors that occur in the root layout.tsx itself.
 * Next.js's app/error.tsx cannot catch these — only global-error.tsx can.
 *
 * IMPORTANT: This component must include its own <html> and <body> tags
 * because it replaces the entire root layout when triggered.
 *
 * @see https://nextjs.org/docs/app/building-your-application/routing/error-handling#handling-global-errors
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Report the root layout error to PostHog Error Tracking
    posthog.captureException(error, {
      source: "global_error_boundary",
      digest: error.digest,
    });
  }, [error]);

  return (
    <html lang="en">
      <body
        style={{
          margin: 0,
          fontFamily:
            "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
          background: "#F0F2F5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          padding: "1rem",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "24px",
            padding: "2rem",
            maxWidth: "400px",
            width: "100%",
            textAlign: "center",
            boxShadow: "0 20px 60px -15px rgba(0,0,0,0.1)",
          }}
        >
          {/* Icon */}
          <div
            style={{
              width: "72px",
              height: "72px",
              background: "linear-gradient(135deg, #f97316, #ef4444)",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 1.5rem",
            }}
          >
            <AlertTriangle size={36} color="white" />
          </div>

          <h1
            style={{ fontSize: "1.5rem", fontWeight: 700, margin: "0 0 0.5rem" }}
          >
            Something went wrong
          </h1>

          <p style={{ color: "#6b7280", margin: "0 0 2rem", fontSize: "0.9rem" }}>
            An unexpected error occurred. Our team has been notified.
          </p>

          <button
            onClick={reset}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              background: "linear-gradient(135deg, #4c6ef5, #7048e8)",
              color: "white",
              border: "none",
              borderRadius: "12px",
              padding: "12px 24px",
              fontSize: "1rem",
              fontWeight: 600,
              cursor: "pointer",
              width: "100%",
              justifyContent: "center",
            }}
          >
            <RefreshCw size={18} />
            Try Again
          </button>
        </div>
      </body>
    </html>
  );
}
